import { useState } from "react";
import { Image as ImageIcon, FileText, Type, BookOpen, Wand2, Sparkles, Layers as LayersIcon, Loader2 } from "lucide-react";
import { cn } from "../../../lib/utils";
import { api } from "../../../lib/apiClient";
import {
  ImportFileModal, TextToImageModal, KdpPreflightModal, PrintPackageModal,
} from "./QuickActionsPhase11";
import { ExportBookModal } from "../ExportBookModal";

const TABS = [
  { id: "props", label: "Properties", icon: LayersIcon },
  { id: "text", label: "Text", icon: Type },
  { id: "image", label: "Image", icon: ImageIcon },
  { id: "page", label: "Page", icon: FileText },
  { id: "ai", label: "AI", icon: Sparkles },
];

export default function RightPropertiesPanel({ collapsed, book, bookId, onBookReload }) {
  const [tab, setTab] = useState("props");
  if (collapsed) return null;

  return (
    <aside
      data-testid="editor-right-panel"
      className="w-[300px] shrink-0 border-l border-[var(--pl-border)] bg-[var(--pl-surface)]/55 backdrop-blur-md flex flex-col"
      style={{ height: "100%" }}
    >
      <div className="flex border-b border-[var(--pl-border)]">
        {TABS.map((t) => {
          const I = t.icon;
          const Active = tab === t.id;
          return (
            <button
              key={t.id}
              data-testid={`right-tab-${t.id}`}
              onClick={() => setTab(t.id)}
              title={t.label}
              className={cn(
                "flex-1 h-9 inline-flex items-center justify-center gap-1.5 text-[11px] transition relative",
                Active ? "text-[var(--pl-aqua)]" : "text-[var(--pl-text-muted)] hover:text-[var(--pl-text)]"
              )}
            >
              <I className="h-3.5 w-3.5" />
              <span className="hidden xl:inline">{t.label}</span>
              {Active && <span className="absolute bottom-0 left-2 right-2 h-0.5 bg-[var(--pl-aqua)] rounded-full" />}
            </button>
          );
        })}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-4 pl-noscroll">
        {tab === "props" && <PropsTab book={book} bookId={bookId} onBookReload={onBookReload} />}
        {tab === "text" && <TextTab />}
        {tab === "image" && <ImageTab />}
        {tab === "page" && <PageTab />}
        {tab === "ai" && <AITab />}
      </div>
    </aside>
  );
}

const Eyebrow = ({ children }) => (
  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--pl-aqua)] mb-1.5">{children}</div>
);

const Row = ({ children }) => <div className="grid grid-cols-2 gap-2">{children}</div>;

const Field = ({ label, children }) => (
  <label className="block">
    <div className="text-[9px] uppercase tracking-[0.18em] text-[var(--pl-text-muted)] mb-1">{label}</div>
    {children}
  </label>
);

const SInput = ({ defaultValue, ...rest }) => (
  <input
    defaultValue={defaultValue}
    style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
    className="w-full h-8 rounded-md border border-[var(--pl-border)] px-2 text-xs"
    {...rest}
  />
);

const SSel = ({ defaultValue, options }) => (
  <select
    defaultValue={defaultValue}
    style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
    className="w-full h-8 rounded-md border border-[var(--pl-border)] px-2 text-xs"
  >
    {options.map((o) => <option key={o}>{o}</option>)}
  </select>
);

const Chip = ({ children, active }) => (
  <button
    className={cn(
      "h-7 px-2.5 rounded-md text-[11px] border transition",
      active
        ? "bg-[var(--pl-aqua)]/15 border-[var(--pl-aqua)]/40 text-[var(--pl-aqua)]"
        : "border-[var(--pl-border)] text-[var(--pl-text)] hover:border-[var(--pl-aqua)]/40"
    )}
  >
    {children}
  </button>
);

function PropsTab({ book, bookId, onBookReload }) {
  const [busy, setBusy] = useState({}); const [flash, setFlash] = useState(null);
  const [modal, setModal] = useState(null);   // "import" | "image" | "kdp" | "print" | "export"
  const setB = (k) => (v) => setBusy((b) => ({ ...b, [k]: v }));

  // If no bookId in URL, buttons show a "Coming in Phase 1.x" hint (no dead clicks).
  const canWire = !!bookId;

  const generateChapter = async () => {
    if (!book?.chapters?.length) { setFlash({ err: "Generate the outline first from the AI runner." }); return; }
    const first = book.chapters.find((c) => c.status !== "completed") || book.chapters[0];
    if (first.status === "completed" && !window.confirm(`Regenerate "${first.title}"? Current content will be replaced (undo via chapter's saved version in AI runner).`)) return;
    setB("gen")(true); setFlash(null);
    try {
      const r = await api.post(`/api/book-lab/books/${bookId}/generate-chapter`, { chapter_id: first.id });
      setFlash({ ok: `Chapter "${first.title}" generated (${r.used_provider || "openai"}).` });
      await onBookReload?.();
    } catch (e) { setFlash({ err: e.message || "Generation failed" }); }
    finally { setB("gen")(false); }
  };

  const noBookFlash = () => setFlash({ err: "Open a book (top-right AI runner → Open in Canvas) to enable this action." });

  const actions = [
    { key: "import",   label: "Import file",       onClick: canWire ? () => setModal("import") : noBookFlash, testId: "qa-import" },
    { key: "img",      label: "Text to image",     onClick: canWire ? () => setModal("image")  : noBookFlash, testId: "qa-text2image" },
    { key: "gen",      label: "Generate chapter",  onClick: canWire ? generateChapter : noBookFlash, testId: "qa-generate-chapter", disabled: busy.gen || !canWire },
    { key: "kdp",      label: "KDP check",         onClick: canWire ? () => setModal("kdp")    : noBookFlash, testId: "qa-kdp" },
    { key: "pdf",      label: "Export book",       onClick: canWire ? () => setModal("export") : noBookFlash, testId: "qa-export-pdf" },
    { key: "pkg",      label: "Print package",     onClick: canWire ? () => setModal("print")  : noBookFlash, testId: "qa-print-package" },
  ];

  return (
    <>
      <section><Eyebrow>Document</Eyebrow>
        {book && (
          <div style={{ fontSize: 10.5, color: "var(--pl-text-muted)", marginBottom: 8, lineHeight: 1.55 }}>
            <div style={{ color: "var(--pl-text)", fontWeight: 700, fontSize: 12 }}>{book.title}</div>
            <div>{book.chapters?.length || 0} chapters · status <strong>{book.status}</strong></div>
          </div>
        )}
        <Row key={book?.id || "no-book"}>
          <Field label="Size"><SSel defaultValue="6 × 9 in" options={["6 × 9 in", "5.5 × 8.5 in", "A4"]} /></Field>
          <Field label="Pages"><SInput defaultValue={book ? String((book.chapters?.length || 0) + 2) : "24"} /></Field>
          <Field label="Columns"><SInput defaultValue="1" /></Field>
          <Field label="Gutter"><SInput defaultValue="0.17" /></Field>
        </Row>
      </section>
      <section><Eyebrow>Quick actions</Eyebrow>
        <div className="grid grid-cols-2 gap-2">
          {actions.map((a) => (
            <button key={a.key} onClick={a.onClick} disabled={a.disabled} data-testid={a.testId}
              className={cn(
                "h-7 px-2.5 rounded-md text-[11px] border transition inline-flex items-center justify-center gap-1",
                a.disabled ? "opacity-50 cursor-not-allowed" : "hover:border-[var(--pl-aqua)]/40",
                "border-[var(--pl-border)] text-[var(--pl-text)]"
              )}>
              {busy[a.key] && <Loader2 className="h-3 w-3 animate-spin" />}
              {a.label}
            </button>
          ))}
        </div>
        {flash && (
          <div data-testid="qa-flash" style={{
            marginTop: 8, padding: "6px 8px", fontSize: 10.5, borderRadius: 6,
            background: flash.err ? "rgba(220,38,38,0.12)" : "rgba(34,197,94,0.12)",
            color: flash.err ? "#FCA5A5" : "#86EFAC", lineHeight: 1.45,
          }}>{flash.err || flash.ok}</div>
        )}
      </section>
      <section><Eyebrow>Rulers & guides</Eyebrow>
        <div className="flex flex-wrap gap-1.5">
          {["Rulers", "Margins", "Columns", "Bleed", "Safe zone"].map((g) => <Chip key={g} active={g === "Rulers" || g === "Margins"}>{g}</Chip>)}
        </div>
      </section>

      {modal === "import" && (
        <ImportFileModal bookId={bookId} onClose={() => setModal(null)}
                         onDone={onBookReload} />
      )}
      {modal === "image" && (
        <TextToImageModal bookId={bookId} book={book} onClose={() => setModal(null)}
                          onDone={onBookReload} />
      )}
      {modal === "kdp" && (
        <KdpPreflightModal bookId={bookId} book={book} onClose={() => setModal(null)} />
      )}
      {modal === "print" && (
        <PrintPackageModal bookId={bookId} book={book} onClose={() => setModal(null)} />
      )}
      {modal === "export" && (
        <ExportBookModal
          bookId={bookId}
          bookTitle={book?.title}
          onClose={() => setModal(null)}
        />
      )}
    </>
  );
}

function TextTab() {
  return (
    <>
      <section><Eyebrow>Text style</Eyebrow>
        <div className="flex gap-1.5">
          <Chip active>Paragraph styles</Chip>
          <Chip>Character styles</Chip>
        </div>
      </section>
      <section><Eyebrow>Character</Eyebrow>
        <Row>
          <Field label="Font"><SSel defaultValue="Outfit" options={["Outfit", "Playfair Display", "Manrope"]} /></Field>
          <Field label="Weight"><SSel defaultValue="Regular" options={["Light", "Regular", "Medium", "Semibold", "Bold"]} /></Field>
          <Field label="Size"><SInput defaultValue="12" /></Field>
          <Field label="Line height"><SInput defaultValue="16" /></Field>
          <Field label="Kerning"><SInput defaultValue="0" /></Field>
          <Field label="Tracking"><SInput defaultValue="0" /></Field>
        </Row>
      </section>
      <section><Eyebrow>Paragraph</Eyebrow>
        <div className="flex flex-wrap gap-1.5">
          <Chip active>Left</Chip><Chip>Center</Chip><Chip>Right</Chip><Chip>Justify</Chip><Chip>Hyphenate</Chip>
        </div>
      </section>
      <section><Eyebrow>Paragraph styles</Eyebrow>
        <div className="flex flex-wrap gap-1">
          {["Chapter Title", "Body Text", "Body Text Indent", "Bullet Point", "TOC Level 1", "Header 1"].map((s) => (
            <span key={s} className="text-[10px] px-2 py-0.5 rounded-md border border-[var(--pl-border)] bg-white/[0.03] text-[var(--pl-text)]">{s}</span>
          ))}
        </div>
      </section>
      <section><Eyebrow>Quick actions</Eyebrow>
        <div className="grid grid-cols-2 gap-2">
          {["Insert footnote", "Insert endnote", "Special character", "Auto Style", "Placeholder text", "Drop cap"].map((a) => <Chip key={a}>{a}</Chip>)}
        </div>
      </section>
    </>
  );
}

function ImageTab() {
  return (
    <>
      <section><Eyebrow>Image fitting</Eyebrow>
        <div className="grid grid-cols-2 gap-1.5">
          {["Replace", "Fit Proportionally", "Fill Frame", "Center Content", "Crop"].map((a, i) => <Chip key={a} active={i === 1}>{a}</Chip>)}
        </div>
      </section>
      <section><Eyebrow>Text wrap</Eyebrow>
        <div className="flex flex-wrap gap-1.5">
          {["No Wrap", "Bounding Box", "Object Shape", "Jump Object", "Left", "Right", "Both"].map((w, i) => (
            <Chip key={w} active={i === 1}>{w}</Chip>
          ))}
        </div>
      </section>
      <section><Eyebrow>Image styles</Eyebrow>
        <div className="grid grid-cols-2 gap-1.5">
          {["Ink Drawing", "Doodle", "Comic", "Blueprint", "Whiteboard", "Cinematic"].map((s) => <Chip key={s}>{s}</Chip>)}
        </div>
      </section>
      <section><Eyebrow>Actions</Eyebrow>
        <div className="grid grid-cols-2 gap-2">
          <Chip>Caption</Chip><Chip>Remove BG</Chip>
        </div>
      </section>
    </>
  );
}

function PageTab() {
  return (
    <>
      <section><Eyebrow>Page setup</Eyebrow>
        <Row>
          <Field label="Top"><SInput defaultValue="0.75" /></Field>
          <Field label="Bottom"><SInput defaultValue="0.75" /></Field>
          <Field label="Inside"><SInput defaultValue="0.75" /></Field>
          <Field label="Outside"><SInput defaultValue="0.5" /></Field>
          <Field label="Bleed"><SInput defaultValue="0.125" /></Field>
          <Field label="Slug"><SInput defaultValue="0.125" /></Field>
        </Row>
      </section>
      <section><Eyebrow>Columns</Eyebrow>
        <Row>
          <Field label="Count"><SInput defaultValue="1" /></Field>
          <Field label="Gutter"><SInput defaultValue="0.17" /></Field>
        </Row>
      </section>
      <section><Eyebrow>Page numbers</Eyebrow>
        <SSel defaultValue="1, 2, 3…" options={["1, 2, 3…", "i, ii, iii…", "A, B, C…", "Custom prefix…"]} />
      </section>
      <section><Eyebrow>Apply changes</Eyebrow>
        <div className="grid grid-cols-2 gap-2">
          <Chip active>This page</Chip><Chip>Selected pages</Chip><Chip>Chapter</Chip><Chip>Whole book</Chip>
        </div>
      </section>
    </>
  );
}

function AITab() {
  return (
    <>
      <section>
        <Eyebrow>Book AI Assistant</Eyebrow>
        <textarea
          rows={4}
          placeholder="Rewrite this chapter opener in a stronger authority tone…"
          style={{ background: "color-mix(in srgb, var(--pl-surface) 70%, transparent)", color: "var(--pl-text)" }}
          className="w-full rounded-lg border border-[var(--pl-border)] p-2 text-xs placeholder:text-[var(--pl-text-muted)] focus:outline-none focus:border-[var(--pl-aqua)]/50"
        />
        <button
          data-testid="right-ai-generate"
          className="mt-2 w-full h-9 rounded-lg pl-btn-aqua text-xs inline-flex items-center justify-center gap-1.5"
        >
          <Wand2 className="h-3.5 w-3.5" /> Generate
        </button>
      </section>
      <section>
        <Eyebrow>AI tools</Eyebrow>
        <div className="grid grid-cols-2 gap-2">
          {[
            "Generate title", "Rewrite selection", "Add story",
            "Generate diagram", "Cover concept", "Fix typography",
            "Check readability", "KDP-ready", "Balance spread",
            "Pull quote",
          ].map((t) => <Chip key={t}>{t}</Chip>)}
        </div>
      </section>
    </>
  );
}
